import React, { useRef } from 'react';
import { Book, FileCode, Terminal, Sparkles } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const Documentation = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section id="docs" className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-white via-cyan-50/30 to-purple-50/30 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden opacity-[0.06] pointer-events-none select-none">
        {['{', '}', '<', '>', '/', '*', '+', '='].map((symbol, idx) => (
          <div
            key={idx}
            className="absolute text-9xl font-mono text-purple-600"
            style={{
              left: `${(idx * 13 + 7) % 88}%`,
              top: `${(idx * 19 + 5) % 82}%`,
            }}
          >
            {symbol}
          </div>
        ))}
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 md:mb-20">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 mb-4"
          >
            <Sparkles className="w-6 h-6 text-purple-600" />
            <span className="px-6 py-2 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-full border border-purple-300/30 text-purple-700 font-bold text-sm tracking-widest">
              DOCUMENTATION
            </span>
            <Sparkles className="w-6 h-6 text-cyan-600" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            INTEGRATION{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600">
              DOCS
            </span>
          </motion.h2>
          
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 mx-auto rounded-full mb-4"
          ></motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto text-justify hyphens-auto"
          >
            Comprehensive guides and APIs to help you integrate quickly
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Code Preview */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="order-2 md:order-1"
          >
            <motion.div
              whileHover={{ scale: 1.02, rotateY: 5 }}
              className="relative bg-gradient-to-br from-gray-900 to-indigo-900 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl overflow-hidden"
            >
              {/* Terminal header */}
              <div className="flex items-center space-x-2 mb-6">
                <motion.div whileHover={{ scale: 1.5 }} className="w-3 h-3 bg-red-500 rounded-full"></motion.div>
                <motion.div whileHover={{ scale: 1.5 }} className="w-3 h-3 bg-yellow-500 rounded-full"></motion.div>
                <motion.div whileHover={{ scale: 1.5 }} className="w-3 h-3 bg-green-500 rounded-full"></motion.div>
              </div>
              
              {/* Code */}
              <div className="space-y-3 font-mono text-sm">
                {[
                  <div key="1" className="text-gray-400">// Initialize payment gateway</div>,
                  <div key="2">
                    <span className="text-purple-400">const</span>
                    <span className="text-blue-400"> gateway</span>
                    <span className="text-gray-300"> = </span>
                    <span className="text-yellow-400">new</span>
                    <span className="text-green-400"> TransactWorld</span>
                    <span className="text-gray-300">(&#123;</span>
                  </div>,
                  <div key="3" className="pl-4">
                    <span className="text-orange-400">apiKey</span>
                    <span className="text-gray-300">: </span>
                    <span className="text-green-300">'your_api_key'</span>
                    <span className="text-gray-300">,</span>
                  </div>,
                  <div key="4" className="pl-4">
                    <span className="text-orange-400">environment</span>
                    <span className="text-gray-300">: </span>
                    <span className="text-green-300">'production'</span>
                  </div>,
                  <div key="5"><span className="text-gray-300">&#125;);</span></div>,
                  <div key="6" className="mt-4 text-gray-400">// Process payment</div>,
                  <div key="7">
                    <span className="text-purple-400">await</span>
                    <span className="text-gray-300"> gateway.</span>
                    <span className="text-blue-400">processPayment</span>
                    <span className="text-gray-300">(&#123;...&#125;);</span>
                  </div>
                ].map((line, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ delay: 0.8 + idx * 0.1 }}
                  >
                    {line}
                  </motion.div>
                ))}
              </div>
              
              <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 align-middle" aria-hidden />

              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/25 rounded-full blur-3xl pointer-events-none" />
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="order-1 md:order-2"
          >
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4 sm:mb-6" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              Quick & Easy Integration
            </h3>
            <p className="text-gray-700 leading-relaxed mb-8 text-base sm:text-lg text-justify hyphens-auto">
              The TransactWorld Platform provides powerful transaction processing APIs and various other modes of integration. Our comprehensive documentation provides all necessary information for a seamless integration.
            </p>

            {/* Features */}
            <div className="space-y-6 mb-8">
              {[
                { icon: Book, title: 'Comprehensive Guides', desc: 'Step-by-step tutorials for all integration methods', color: 'from-purple-500 to-pink-500' },
                { icon: FileCode, title: 'Code Examples', desc: 'Ready-to-use code snippets in multiple languages', color: 'from-cyan-500 to-blue-500' },
                { icon: Terminal, title: 'REST API', desc: 'RESTful API for seamless integration', color: 'from-pink-500 to-orange-500' }
              ].map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ delay: 0.9 + idx * 0.1 }}
                  whileHover={{ x: 10, scale: 1.02 }}
                  className="flex items-start space-x-4 p-4 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-2xl border border-purple-200/30"
                >
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.6 }}
                    className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-gray-600 text-sm text-justify hyphens-auto">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(139, 92, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="relative px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 text-white rounded-xl font-bold shadow-xl overflow-hidden group"
              onClick={() => window.open('https://docs.transactworld.com/', '_blank')}
            >
              <span className="relative z-10">View Documentation</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              ></motion.div>
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Documentation;